/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Argon Dashboard 2 MUI components

// Argon Dashboard 2 MUI examples

import ArgonButton from "components/ArgonButton";
import {
  FormControl,
  Select,
  LinearProgress,
  MenuItem,
  InputLabel,
  Chip,
  Button,
} from "@mui/material";
import { Person, Refresh, FilterList } from "@mui/icons-material";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";

import ShortListTutorsTableData from "Views/Activities/ShortListTutors/data/ShortListTableData";
import AttendanceListViewTutorSummaryTableData from "Views/Activities/AllAttendance/data/AttendanceListViewTutorSummaryTableData";

import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";

// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"

import { set } from "date-fns";
import Dropdown from "components/CustomDropdown/Dropdown";
import ArgonInput from "components/ArgonInput";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";

import { useState, useEffect } from "react";
import axios from "utils/api";

import AttendanceTableData from "Views/Activities/AllAttendance/data/AttendanceTableData";
import { Checkbox, FormControlLabel } from "@mui/material";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import ArgonBadge from "components/ArgonBadge";
/* eslint-disable react/prop-types */
function Tables({ classData }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
    regular: 0,
    extra: 0,
    recovery: 0,
  });

  // Fetch attendance data for this class session
  useEffect(() => {
    if (classData?._id) {
      fetchAttendanceData();
    }
  }, [classData?._id]);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/attendance`, {
        params: {
          classSessionId: classData._id,
        },
      });
      if (response.data.success) {
        // Backend returns 'attendances' array, not 'data'
        const attendances = response.data.attendances || response.data.data || [];
        setAttendanceData(attendances);
        console.log("Attendance data:", attendances);

        // Calculate statistics like the main attendance view
        const computedStats = {
          total: attendances.length,
          present: attendances.filter((a) => a.status === "PRESENT").length,
          absent: attendances.filter((a) => a.status === "ABSENT").length,
          regular: attendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("REGULAR") : a.type === "REGULAR"
          ).length,
          extra: attendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("EXTRA") : a.type === "EXTRA"
          ).length,
          recovery: attendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("RECOVERY") : a.type === "RECOVERY"
          ).length,
        };

        // Update stats state
        setStats(computedStats);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  // Create table data from attendance - matching main attendance view structure
  const tableData = {
    columns: [
      { name: "sno", align: "center" },
      { name: "date", align: "left" },
      { name: "startTime", align: "left" },
      { name: "endTime", align: "left" },
      { name: "duration", align: "left" },
      { name: "type", align: "center" },
      { name: "status", align: "center" },
      { name: "topic", align: "left" },
      { name: "homework", align: "left" },
    ],

    rows: attendanceData.map((att, index) => ({
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      date: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.startTime
            ? new Date(att.startTime).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A"}
        </ArgonTypography>
      ),
      startTime: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.startTime
            ? new Date(att.startTime).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A"}
        </ArgonTypography>
      ),
      endTime: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.attendanceTime
            ? new Date(att.attendanceTime).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "Ongoing"}
        </ArgonTypography>
      ),
      duration: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.duration
            ? att.duration + " min"
            : att.startTime && att.attendanceTime
            ? Math.round((new Date(att.attendanceTime) - new Date(att.startTime)) / (1000 * 60)) +
              " min"
            : "N/A"}
        </ArgonTypography>
      ),
      type: (
        <ArgonBadge
          variant="gradient"
          badgeContent={Array.isArray(att.type) ? att.type.join(", ") : att.type || "REGULAR"}
          color={
            Array.isArray(att.type)
              ? att.type.includes("EXTRA")
                ? "info"
                : att.type.includes("RECOVERY")
                ? "warning"
                : "success"
              : att.type === "EXTRA"
              ? "info"
              : att.type === "RECOVERY"
              ? "warning"
              : "success"
          }
          size="xs"
          container
        />
      ),
      status: (
        <ArgonBadge
          variant="gradient"
          badgeContent={att.attendanceTime ? "Completed" : "Ongoing"}
          color={att.attendanceTime ? "success" : "warning"}
          size="xs"
          container
        />
      ),
      topic: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.topicCovered || "N/A"}
        </ArgonTypography>
      ),
      homework: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.homeWorkStatus ? "Assigned" : "Not Assigned"}
        </ArgonTypography>
      ),
    })),
  };

  const handleDeleteClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Get data from classData or use default values - matching main attendance view
  const uniqueSessions = new Set(
    attendanceData
      .filter((a) => a.attendanceTime)
      .map((a) => new Date(a.attendanceTime).toDateString())
  );

  const completedClasses = classData?.completedClasses ?? uniqueSessions.size;
  const totalClasses = classData?.totalNoOfClasses ?? completedClasses;
  const extraClasses = classData?.extraClasses ?? stats.extra;
  const recoveryClasses = classData?.recoveryClasses ?? stats.recovery;
  const remainingClasses = Math.max(0, totalClasses - completedClasses);

  const sortedAttendances = attendanceData
    .filter((attendance) => attendance.attendanceTime)
    .slice()
    .sort((a, b) => new Date(a.attendanceTime).getTime() - new Date(b.attendanceTime).getTime());

  const firstAttendance = sortedAttendances[0];
  const lastAttendance = sortedAttendances[sortedAttendances.length - 1];

  const data = {
    completedSessions: completedClasses,
    totalSessions: totalClasses,
    remainingSessions: remainingClasses,
    extraClasses,
    recoveryClasses,
    startDate: firstAttendance
      ? new Date(firstAttendance.attendanceTime).toLocaleDateString("en-IN")
      : classData?.firstClassOn
      ? new Date(classData.firstClassOn).toLocaleDateString("en-IN")
      : "N/A",
    endDate: lastAttendance
      ? new Date(lastAttendance.attendanceTime).toLocaleDateString("en-IN")
      : "N/A",
    classStatus: classData?.status || "CREATED",
    cyclePaymentStatus: classData?.paymentStatus || "PENDING",
    remark:
      totalClasses === 0
        ? "Total classes not set. Please update class session details."
        : "Attendance tracking in progress",
  };

  const progress = data.totalSessions > 0 ? (data.completedSessions / data.totalSessions) * 100 : 0;

  const [student, setStudent] = useState("");
  const [cycle, setCycle] = useState("");

  // Get students list for dropdown
  const studentsList = classData?.students || [];
  const studentOptions = studentsList.map((s) => s.name || "N/A");

  // Get cycles list for dropdown
  const cyclesList = classData?.cycles || [];
  const cycleOptions = cyclesList.map((c) => c.name || "N/A");

  return (
    <ArgonBox mb={3}>
      <Card>
        <ArgonBox p={3}>
          <ArgonTypography mb={2} variant="h6" fontWeight="medium">
            Attendance
          </ArgonTypography>

          {/* Filter Controls */}
          <ArgonBox
            sx={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              mb: 3,
            }}
          >
            <ArgonBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
              {/* Student Dropdown */}
              <ArgonBox>
                <ArgonTypography variant="body2" fontWeight="medium" color="text.primary" mb={1}>
                  Select Student
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={student}
                  options={studentOptions.length > 0 ? studentOptions : ["No students"]}
                  onChange={(e) => setStudent(e.target.value)}
                  width={200}
                />
              </ArgonBox>

              {/* Cycle Dropdown */}
              <ArgonBox>
                <ArgonTypography variant="body2" fontWeight="medium" color="text.primary" mb={1}>
                  Select Cycle
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={cycle}
                  options={cycleOptions.length > 0 ? cycleOptions : ["No cycles"]}
                  width={200}
                  onChange={(e) => setCycle(e.target.value)}
                />
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>

          {/* Attendance Card */}
          <Card
            sx={{
              borderRadius: "20px",
              background: "linear-gradient(135deg, #f5f7fa 0%, #eef1f5ff 100%)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
              p: 4,
              border: "1px solid #e0e0e0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative Background Element */}
            <ArgonBox
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                background: "linear-gradient(45deg, #efefefff, #f2f2f2ff)",
                borderRadius: "50%",
                opacity: 0.1,
              }}
            />

            {/* Header */}
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              flexWrap="wrap"
            >
              <ArgonBox display="flex" alignItems="center" gap={2}>
                <ArgonBox
                  sx={{
                    background: "linear-gradient(45deg, #e2e2e2ff, #764ba2)",
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  {/* <CalendarToday sx={{ color: "white", fontSize: "20px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    Attendance Overview
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Track your learning progress
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox display="flex" gap={2} flexWrap="wrap">
                <ArgonBox display="flex" alignItems="center" gap={1}>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                    Class Status:
                  </ArgonTypography>
                  <Chip
                    label={data.classStatus === "SCHEDULED" ? "Active" : data.classStatus}
                    sx={{
                      fontSize: 12,
                      fontWeight: "bold",
                      color: "white !important",
                      background: "linear-gradient(45deg, #4CAF50, #45a049)",
                      borderRadius: "12px",
                      px: 2,
                      boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
                    }}
                  />
                </ArgonBox>

                <ArgonBox display="flex" alignItems="center" gap={1}>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                    Cycle Payment:
                  </ArgonTypography>
                  <Chip
                    label={data.cyclePaymentStatus}
                    sx={{
                      fontSize: 12,
                      fontWeight: "bold",
                      color: "white !important",
                      background: "linear-gradient(45deg, #FF9800, #F57C00)",
                      borderRadius: "12px",
                      px: 2,
                      boxShadow: "0 4px 15px rgba(255, 152, 0, 0.3)",
                    }}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {/* Progress Section */}
            <ArgonBox
              sx={{
                background: "white",
                borderRadius: "16px",
                p: 3,
                mb: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <ArgonBox display="flex" alignItems="center" gap={2} mb={2}>
                {/* <TrendingUp sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                  Session Progress
                </ArgonTypography>
              </ArgonBox>

              {data.totalSessions === 0 ? (
                <ArgonBox
                  sx={{
                    background: "#fff3cd",
                    borderRadius: "8px",
                    p: 2,
                    border: "1px solid #ffc107",
                  }}
                >
                  <ArgonTypography variant="body2" color="warning" fontWeight="medium">
                    ⚠️ Total classes not configured. Current completed classes:{" "}
                    {data.completedSessions}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <>
                  <ArgonBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                      Sessions Completed: {data.completedSessions}/{data.totalSessions}
                    </ArgonTypography>
                    <ArgonTypography variant="h6" fontWeight="bold" color="#4CAF50">
                      {Math.round(progress)}%
                    </ArgonTypography>
                  </ArgonBox>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      background: "#e0e0e0",
                      width: "100%",
                      "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(45deg, #4CAF50, #45a049)",
                        borderRadius: 6,
                        width: `${progress}%`,
                      },
                    }}
                  />
                </>
              )}
            </ArgonBox>

            {/* Details Grid */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Total Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#2196F3">
                    {stats.total}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Regular Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#4CAF50">
                    {stats.regular}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Extra Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#FF9800">
                    {stats.extra}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Recovery Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#F44336">
                    {stats.recovery}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>
            </Grid>

            {/* Remark Section */}
            <ArgonBox
              sx={{
                background: "white",
                borderRadius: "16px",
                p: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <ArgonBox
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  {/* <Comment sx={{ color: "#9C27B0", fontSize: "20px" }} /> */}
                  <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                    Remarks
                  </ArgonTypography>
                </ArgonBox>

                {/* <ArgonButton
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={handleDeleteClick}
                >
                  Edit Remark
                </ArgonButton> */}
              </ArgonBox>

              <ArgonTypography
                variant="body2"
                color="medium"
                sx={{
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  p: 1,
                  border: "1px solid #e9ecef",
                  fontStyle: "italic",
                }}
              >
                {data.remark}
              </ArgonTypography>
            </ArgonBox>
          </Card>

          <Card
            sx={{
              borderRadius: "20px",
              border: "1px solid #e0e0e0",
              position: "relative",
              overflow: "hidden",
              mt: 3,
            }}
          >
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Attendance List
                </ArgonTypography>
              </ArgonBox>

              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {loading ? (
                  <ArgonBox display="flex" justifyContent="center" p={5}>
                    <ArgonTypography variant="h6">Loading attendance...</ArgonTypography>
                  </ArgonBox>
                ) : attendanceData.length === 0 ? (
                  <ArgonBox display="flex" justifyContent="center" p={5}>
                    <ArgonTypography variant="h6" color="text">
                      No attendance records found
                    </ArgonTypography>
                  </ArgonBox>
                ) : (
                  <Table columns={tableData.columns} rows={tableData.rows} />
                )}
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>

        {/* Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            <ArgonTypography variant="h6" fontWeight="medium">
              Edit Remark
            </ArgonTypography>
          </DialogTitle>

          <DialogContent dividers>
            <ArgonInput fullWidth multiline minRows={4} placeholder="Your Remark" sx={{ mt: 1 }} />
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <ArgonButton onClick={handleClose} variant="outlined" color="secondary">
              Cancel
            </ArgonButton>
            <ArgonButton variant="contained" color="primary">
              Save
            </ArgonButton>
          </DialogActions>
        </Dialog>
      </Card>
    </ArgonBox>
  );
}

export default Tables;
